import Phaser from 'phaser';
import {
  ComponentType,
  ComponentCategory,
  COMPONENT_DEFS,
  WeaponTarget,
} from '@/components/ComponentDefs';
import { COMPONENT_SPACING, TILE_SIZE, CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { createComponentVisual } from '@/systems/ComponentIcons';
import { getOverride } from '@/config/ComponentOverrides';
import { ProgressionManager, GRID_MAX_SIZE } from '@/systems/ProgressionManager';
import { SoundManager } from '@/systems/SoundManager';
import { GOD } from '@/config/GodMode';
import {
  edgesCompatible,
  getTransformedEdges,
  getTransformedForbiddenEdges,
  type PlacementCallbacks,
} from './PlacementValidator';

// ── Layout constants ──
const MARGIN = 12;
const RULES_PANEL_W = 370;
const STATS_PANEL_W = 220;
const PANEL_GAP = 16;
const TITLE_H = 56;
const CELL_MAX = 86;
const CELL_MIN = 36;
const PANEL_BG = 0x0d0d1a;
const PANEL_BORDER = 0x333366;

// Palette constants
const PALETTE_CARD_H = 100;
const PALETTE_HEADER_H = 18;
const PALETTE_TOTAL_H = PALETTE_HEADER_H + PALETTE_CARD_H + 12;

/** Mutable layout state — recomputed when grid size changes. */
export interface GridLayout {
  cols: number;
  rows: number;
  cellSize: number;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
  belowGridY: number;
  topSectionH: number;
  statsX: number;
  statsY: number;
  rulesX: number;
  paletteY: number;
}

/**
 * Apply rotation to a grid component visual.
 * Composes grid rotation with the component's defaultRotation from COMP GOD.
 */
function applyGridRotationFn(
  visual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite,
  type: ComponentType,
  rotation: number,
  evoLevel?: number,
): void {
  const level = evoLevel ?? ProgressionManager.getComponentLevel(type);
  const ov = getOverride(type, level);
  const defRot = ov?.defaultRotation ?? COMPONENT_DEFS[type].defaultRotation;

  if (type === ComponentType.DIRECTION) {
    const norm = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const eps = 0.1;
    const absX = Math.abs(visual.scaleX);
    const absY = Math.abs(visual.scaleY);
    if (Math.abs(norm - Math.PI) < eps) {
      visual.setRotation(defRot);
      visual.setScale(-absX, absY);
    } else if (norm < eps || Math.abs(norm - Math.PI * 2) < eps) {
      visual.setRotation(defRot);
      visual.setScale(absX, absY);
    } else if (Math.abs(norm - Math.PI / 2) < eps) {
      visual.setRotation(Math.PI / 2 + defRot);
      visual.setScale(absX, absY);
    } else if (Math.abs(norm - Math.PI * 3 / 2) < eps) {
      visual.setRotation(-Math.PI / 2 + defRot);
      visual.setScale(absX, absY);
    } else {
      visual.setRotation(rotation + defRot);
      visual.setScale(absX, absY);
    }
  } else {
    visual.setRotation(rotation + defRot);
  }
}

/** Position a component visual in its grid cell, rotating offset around cell center. */
function positionInCellFn(
  visual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite,
  cx: number, cy: number,
  type: ComponentType,
  rotation: number,
  cellSize: number,
  evoLevel?: number,
): void {
  const def = COMPONENT_DEFS[type];
  const level = evoLevel ?? ProgressionManager.getComponentLevel(type);
  const ov = getOverride(type, level);
  const defRot = ov?.defaultRotation ?? def.defaultRotation;
  const totalRot = rotation + defRot;
  const offScale = cellSize / 90;
  const rawOx = (ov?.offsetX ?? def.offsetX) * offScale;
  const rawOy = (ov?.offsetY ?? def.offsetY) * offScale;
  if (rawOx === 0 && rawOy === 0) {
    visual.setPosition(cx, cy);
    return;
  }
  const cos = Math.cos(totalRot);
  const sin = Math.sin(totalRot);
  visual.setPosition(cx + rawOx * cos - rawOy * sin, cy + rawOx * sin + rawOy * cos);
}

export class GridManager {
  // Grid state
  private grid: (ComponentType | null)[][] = [];
  private gridRotation: number[][] = [];
  private gridFlipX: boolean[][] = [];
  private gridFlipY: boolean[][] = [];
  private gridEvoLevel: number[][] = [];
  private gridCellGfx: (Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite | null)[][] = [];
  private gridBgRects: Phaser.GameObjects.Rectangle[][] = [];

  /** When true, removeFromGrid/placeComponent skip recalcStats/drawConnections/refreshRuleDisplays. */
  batchMode = false;

  // Visual overlays
  comMarker!: Phaser.GameObjects.Graphics;
  sensorOverlay!: Phaser.GameObjects.Graphics;
  sensorRangeLabel!: Phaser.GameObjects.Text;
  comLabel!: Phaser.GameObjects.Text;
  connGfx!: Phaser.GameObjects.Graphics;
  dirArrowGfx!: Phaser.GameObjects.Graphics;
  isolatedOverlays: Phaser.GameObjects.Rectangle[] = [];
  hoverCell: { r: number; c: number } | null = null;
  hoverRect: Phaser.GameObjects.Rectangle | null = null;

  // Grid expand objects
  private gridExpandObjects: Phaser.GameObjects.GameObject[] = [];

  // Layout
  private layout: GridLayout;

  constructor(private scene: Phaser.Scene) {
    this.layout = this.computeLayout(ProgressionManager.gridCols, ProgressionManager.gridRows);
  }

  private computeLayout(cols: number, rows: number): GridLayout {
    const paletteY = CANVAS_HEIGHT - PALETTE_TOTAL_H - 8;
    const topSectionH = paletteY - 64 - TITLE_H;
    const rulesX = MARGIN;
    const statsX = CANVAS_WIDTH - MARGIN - STATS_PANEL_W;
    const availableW = statsX - (rulesX + RULES_PANEL_W) - PANEL_GAP * 2;

    let cellSize = Math.min(
      Math.floor(topSectionH / rows),
      Math.floor(availableW / cols),
      CELL_MAX,
    );
    cellSize = Math.max(cellSize, CELL_MIN);

    // Shrink grid by 20%
    cellSize = Math.floor(cellSize * 0.8);
    cellSize = Math.max(cellSize, CELL_MIN);

    const gridW = cols * cellSize;
    const gridH = rows * cellSize;

    const gridAreaLeft = rulesX + RULES_PANEL_W + PANEL_GAP;
    const gridAreaRight = statsX - PANEL_GAP;
    const gridX = gridAreaLeft + Math.floor((gridAreaRight - gridAreaLeft - gridW) / 2);
    const gridY = TITLE_H + Math.floor((topSectionH - gridH) / 2) - 50;

    return {
      cols, rows, cellSize,
      gridX, gridY, gridW, gridH,
      belowGridY: gridY + gridH + 6,
      topSectionH,
      statsX, statsY: TITLE_H,
      rulesX,
      paletteY,
    };
  }

  /** Recompute layout for given grid dimensions */
  recomputeLayout(cols: number, rows: number): void {
    this.layout = this.computeLayout(cols, rows);
  }

  getLayout(): { cellSize: number; gridX: number; gridY: number; gridW: number; gridH: number; belowGridY: number; topSectionH: number; statsX: number; statsY: number; rulesX: number; paletteY: number; cols: number; rows: number } {
    return this.layout;
  }

  getDimensions(): { rows: number; cols: number } {
    return { rows: this.layout.rows, cols: this.layout.cols };
  }

  // ── Grid state accessors ──

  getCell(r: number, c: number): ComponentType | null { return this.grid[r]?.[c] ?? null; }
  getRotation(r: number, c: number): number { return this.gridRotation[r]?.[c] ?? 0; }
  getFlipX(r: number, c: number): boolean { return this.gridFlipX[r]?.[c] ?? false; }
  getFlipY(r: number, c: number): boolean { return this.gridFlipY[r]?.[c] ?? false; }
  getEvoLevel(r: number, c: number): number { return this.gridEvoLevel[r]?.[c] ?? 0; }
  getGridState(): (ComponentType | null)[][] { return this.grid; }

  setRotation(r: number, c: number, val: number): void { this.gridRotation[r][c] = val; }
  setFlipX(r: number, c: number, val: boolean): void { this.gridFlipX[r][c] = val; }
  setFlipY(r: number, c: number, val: boolean): void { this.gridFlipY[r][c] = val; }
  setEvoLevel(r: number, c: number, val: number): void { this.gridEvoLevel[r][c] = val; }

  /** Expose applyGridRotation so PlacementValidator can use it for drag ghosts */
  applyGridRotation(
    visual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite,
    type: ComponentType,
    rotation: number,
    evoLevel?: number,
  ): void {
    applyGridRotationFn(visual, type, rotation, evoLevel);
  }

  screenToGrid(sx: number, sy: number): { r: number; c: number } | null {
    const { gridX, gridY, cellSize, rows, cols } = this.layout;
    const c = Math.floor((sx - gridX) / cellSize);
    const r = Math.floor((sy - gridY) / cellSize);
    if (r >= 0 && r < rows && c >= 0 && c < cols) return { r, c };
    return null;
  }

  setup(): void {
    // No EventBus listeners — GridManager is called imperatively
  }

  destroy(): void {
    for (const ov of this.isolatedOverlays) ov.destroy();
    this.isolatedOverlays = [];
    for (const obj of this.gridExpandObjects) obj.destroy();
    this.gridExpandObjects = [];
  }

  // ══════════════════════════════════════════
  //  GRID CREATION
  // ══════════════════════════════════════════

  createGrid(onCellPointerDown: (r: number, c: number, pointer: Phaser.Input.Pointer) => void): void {
    const { gridX, gridY, gridW, gridH, cellSize, rows, cols } = this.layout;

    // Grid panel background
    this.scene.add.rectangle(gridX + gridW / 2, gridY + gridH / 2, gridW + 12, gridH + 12, PANEL_BG, 0.85)
      .setStrokeStyle(1, PANEL_BORDER, 0.6).setDepth(1);

    // Connection lines layer
    this.connGfx = this.scene.add.graphics().setDepth(2);
    // Direction arrow overlay
    this.dirArrowGfx = this.scene.add.graphics().setDepth(3);

    const centerCol = Math.floor(cols / 2);
    const centerRow = Math.floor(rows / 2);

    this.grid = [];
    this.gridRotation = [];
    this.gridFlipX = [];
    this.gridFlipY = [];
    this.gridEvoLevel = [];
    this.gridCellGfx = [];
    this.gridBgRects = [];

    for (let r = 0; r < rows; r++) {
      this.grid[r] = [];
      this.gridRotation[r] = [];
      this.gridFlipX[r] = [];
      this.gridFlipY[r] = [];
      this.gridEvoLevel[r] = [];
      this.gridCellGfx[r] = [];
      this.gridBgRects[r] = [];
      for (let c = 0; c < cols; c++) {
        this.grid[r][c] = null;
        this.gridRotation[r][c] = 0;
        this.gridFlipX[r][c] = false;
        this.gridFlipY[r][c] = false;
        this.gridEvoLevel[r][c] = 0;
        this.gridCellGfx[r][c] = null;

        const cx = gridX + c * cellSize + cellSize / 2;
        const cy = gridY + r * cellSize + cellSize / 2;

        const isCenter = r === centerRow && c === centerCol;
        const fillColor = isCenter ? 0x1a1a3a : 0x111125;
        const strokeAlpha = isCenter ? 0.6 : 0.25;

        const rect = this.scene.add.rectangle(cx, cy, cellSize - 2, cellSize - 2, fillColor)
          .setStrokeStyle(1, 0x333366, strokeAlpha).setDepth(2);
        rect.setInteractive({ useHandCursor: true, dropZone: true });

        rect.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          onCellPointerDown(r, c, pointer);
        });

        this.gridBgRects[r][c] = rect;
      }
    }

    // Center marker glow
    const ccx = gridX + centerCol * cellSize + cellSize / 2;
    const ccy = gridY + centerRow * cellSize + cellSize / 2;
    const glow = this.scene.add.circle(ccx, ccy, cellSize / 2 + 2, 0x4466aa, 0.08).setDepth(1);
    this.scene.tweens.add({ targets: glow, alpha: 0.15, duration: 1200, yoyo: true, repeat: -1 });

    // Hover highlight rect
    this.hoverRect = this.scene.add.rectangle(0, 0, cellSize - 2, cellSize - 2, 0x4488cc, 0.15)
      .setStrokeStyle(1, 0x6699cc, 0.4).setDepth(4).setVisible(false);

    // Center of mass indicator
    this.comMarker = this.scene.add.graphics().setDepth(8).setVisible(false);
    this.sensorOverlay = this.scene.add.graphics().setDepth(7);
    // Clip sensor overlay to grid bounds
    const sensorMaskGfx = this.scene.make.graphics({ x: 0, y: 0 });
    sensorMaskGfx.fillRect(gridX, gridY, gridW, gridH);
    this.sensorOverlay.setMask(sensorMaskGfx.createGeometryMask());
    this.sensorRangeLabel = this.scene.add.text(0, 0, '', {
      fontSize: '9px', color: '#44cc44', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#0a0a1e', padding: { x: 3, y: 1 },
    }).setDepth(8).setVisible(false).setOrigin(0.5);
    this.comLabel = this.scene.add.text(0, 0, 'CoM', {
      fontSize: '7px', color: '#ff4466', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 1.4).setDepth(8).setVisible(false);
  }

  // ══════════════════════════════════════════
  //  GRID EXPAND BUTTONS
  // ══════════════════════════════════════════

  createGridExpandButtons(isCampaign: boolean, snapshotEditData: () => object): void {
    for (const obj of this.gridExpandObjects) obj.destroy();
    this.gridExpandObjects = [];

    if (!isCampaign && !GOD.freeBuilding) return;

    const { gridX, gridY, gridW, gridH, cols, rows } = this.layout;

    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '9px', color: '#aaccff', fontFamily: 'monospace', fontStyle: 'bold',
    };
    const costStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '8px', color: '#ffaa44', fontFamily: 'monospace',
    };

    if (cols < GRID_MAX_SIZE) {
      const btnW = 22;
      const btnH = gridH;
      const cx = gridX + gridW + btnW / 2 + 1;
      const cy = gridY + gridH / 2;

      const bg = this.scene.add.rectangle(cx, cy, btnW, btnH, 0x111125, 0.6)
        .setStrokeStyle(1, 0x336699, 0.4).setDepth(2).setInteractive({ useHandCursor: true });
      const label = this.scene.add.text(cx, cy - 8, '+', labelStyle).setOrigin(0.5).setDepth(3);
      const colCostLabel = GOD.freeBuilding ? 'FREE' : `$${ProgressionManager.getExpandColCost()}`;
      const cost = this.scene.add.text(cx, cy + 6, colCostLabel, costStyle).setOrigin(0.5).setDepth(3);

      bg.on('pointerdown', () => {
        if (ProgressionManager.tryExpandCols()) {
          SoundManager.play('clickConfirm');
          this.scene.scene.restart(snapshotEditData());
        } else {
          SoundManager.play('error');
        }
      });
      bg.on('pointerover', () => { bg.setFillStyle(0x223355, 0.8); bg.setStrokeStyle(1, 0x4488cc, 0.7); });
      bg.on('pointerout', () => { bg.setFillStyle(0x111125, 0.6); bg.setStrokeStyle(1, 0x336699, 0.4); });
      this.gridExpandObjects.push(bg, label, cost);
    }

    if (rows < GRID_MAX_SIZE) {
      const btnW = gridW;
      const btnH = 20;
      const cx = gridX + gridW / 2;
      const cy = gridY + gridH + btnH / 2 + 1;

      const bg = this.scene.add.rectangle(cx, cy, btnW, btnH, 0x111125, 0.6)
        .setStrokeStyle(1, 0x336699, 0.4).setDepth(2).setInteractive({ useHandCursor: true });
      const label = this.scene.add.text(cx - 20, cy, '+', labelStyle).setOrigin(0.5).setDepth(3);
      const rowCostLabel = GOD.freeBuilding ? 'FREE' : `$${ProgressionManager.getExpandRowCost()}`;
      const cost = this.scene.add.text(cx + 16, cy, rowCostLabel, costStyle).setOrigin(0.5).setDepth(3);

      bg.on('pointerdown', () => {
        if (ProgressionManager.tryExpandRows()) {
          SoundManager.play('clickConfirm');
          this.scene.scene.restart(snapshotEditData());
        } else {
          SoundManager.play('error');
        }
      });
      bg.on('pointerover', () => { bg.setFillStyle(0x223355, 0.8); bg.setStrokeStyle(1, 0x4488cc, 0.7); });
      bg.on('pointerout', () => { bg.setFillStyle(0x111125, 0.6); bg.setStrokeStyle(1, 0x336699, 0.4); });
      this.gridExpandObjects.push(bg, label, cost);
    }
  }

  // ══════════════════════════════════════════
  //  CELL DRAWING
  // ══════════════════════════════════════════

  /** Place component into grid state arrays and draw its visual. Does NOT do validation. */
  placeComponent(row: number, col: number, type: ComponentType, rotation: number, evoLevel: number): void {
    const { gridX, gridY, cellSize, rows } = this.layout;
    this.grid[row][col] = type;
    this.gridRotation[row][col] = rotation;
    this.gridEvoLevel[row][col] = evoLevel;

    const def = COMPONENT_DEFS[type];
    const cx = gridX + col * cellSize + cellSize / 2;
    const cy = gridY + row * cellSize + cellSize / 2;

    const placeVisual = createComponentVisual(this.scene, type, cellSize, def.color, evoLevel);
    applyGridRotationFn(placeVisual, type, rotation, evoLevel);
    if (this.gridFlipX[row][col]) placeVisual.scaleX *= -1;
    if (this.gridFlipY[row][col]) placeVisual.scaleY *= -1;
    positionInCellFn(placeVisual, cx, cy, type, rotation, cellSize, evoLevel);
    (placeVisual as Phaser.GameObjects.Graphics).setDepth(5 + (rows - row) * 0.1);

    this.gridCellGfx[row][col] = placeVisual;
  }

  /** Redraw a single grid cell visual + refresh connections and stats. */
  redrawCell(r: number, c: number): void {
    if (this.gridCellGfx[r][c]) {
      this.gridCellGfx[r][c]!.destroy();
    }

    const type = this.grid[r][c]!;
    const def = COMPONENT_DEFS[type];
    const { gridX, gridY, cellSize, rows } = this.layout;
    const cx = gridX + c * cellSize + cellSize / 2;
    const cy = gridY + r * cellSize + cellSize / 2;

    const cellVisual = createComponentVisual(this.scene, type, cellSize, def.color, this.gridEvoLevel[r][c]);
    applyGridRotationFn(cellVisual, type, this.gridRotation[r][c], this.gridEvoLevel[r][c]);
    positionInCellFn(cellVisual, cx, cy, type, this.gridRotation[r][c], cellSize, this.gridEvoLevel[r][c]);
    if (this.gridFlipX[r][c]) cellVisual.scaleX *= -1;
    if (this.gridFlipY[r][c]) cellVisual.scaleY *= -1;
    (cellVisual as Phaser.GameObjects.Graphics).setDepth(5 + (rows - r) * 0.1);
    this.gridCellGfx[r][c] = cellVisual;

    this.drawConnections();
    // Note: caller is responsible for calling recalcStats if needed
  }

  removeFromGrid(row: number, col: number, callbacks: PlacementCallbacks): void {
    const type = this.grid[row][col];
    if (type === null) return;

    this.grid[row][col] = null;
    this.gridRotation[row][col] = 0;
    this.gridFlipX[row][col] = false;
    this.gridFlipY[row][col] = false;
    this.gridEvoLevel[row][col] = 0;

    if (this.gridCellGfx[row][col]) {
      this.gridCellGfx[row][col]!.destroy();
      this.gridCellGfx[row][col] = null;
    }

    if (!this.batchMode) {
      callbacks.recalcStats();
      this.drawConnections();
      callbacks.refreshRuleDisplays();
    }
  }

  // ══════════════════════════════════════════
  //  CONNECTIVITY
  // ══════════════════════════════════════════

  /** Returns the set of connected cell keys ("r,c") from BFS starting at the first occupied cell. */
  getConnectedCells(): { connected: Set<string>; occupied: { r: number; c: number }[] } {
    const { rows, cols } = this.layout;
    const occupied: { r: number; c: number }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] !== null) occupied.push({ r, c });
      }
    }
    const connected = new Set<string>();
    if (occupied.length <= 1) {
      for (const o of occupied) connected.add(`${o.r},${o.c}`);
      return { connected, occupied };
    }

    const queue: { r: number; c: number }[] = [occupied[0]];
    connected.add(`${occupied[0].r},${occupied[0].c}`);
    const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]];

    while (queue.length > 0) {
      const cur = queue.shift()!;
      const typeA = this.grid[cur.r][cur.c]!;
      const rotA = this.gridRotation[cur.r][cur.c];
      for (const [dr, dc, dir] of dirs) {
        const nr = cur.r + dr;
        const nc = cur.c + dc;
        const key = `${nr},${nc}`;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        if (connected.has(key)) continue;
        const typeB = this.grid[nr][nc];
        if (typeB === null) continue;
        if (!edgesCompatible(typeA, rotA, typeB, this.gridRotation[nr][nc], dir,
          this.gridFlipX[cur.r][cur.c], this.gridFlipY[cur.r][cur.c],
          this.gridFlipX[nr][nc], this.gridFlipY[nr][nc])) continue;
        // Wheels cannot connect to other wheels
        if (typeA === ComponentType.WHEEL && typeB === ComponentType.WHEEL) continue;
        connected.add(key);
        queue.push({ r: nr, c: nc });
      }
    }
    return { connected, occupied };
  }

  checkConnectivity(): boolean {
    const { connected, occupied } = this.getConnectedCells();
    return connected.size === occupied.length;
  }

  /** Count distinct connected groups on the grid. */
  countDisconnectedGroups(): number {
    const { rows, cols } = this.layout;
    const visited = new Set<string>();
    let groups = 0;
    const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] === null) continue;
        const key = `${r},${c}`;
        if (visited.has(key)) continue;
        groups++;
        const queue: { r: number; c: number }[] = [{ r, c }];
        visited.add(key);
        while (queue.length > 0) {
          const cur = queue.shift()!;
          const typeA = this.grid[cur.r][cur.c]!;
          const rotA = this.gridRotation[cur.r][cur.c];
          for (const [dr, dc, dir] of dirs) {
            const nr = cur.r + dr;
            const nc = cur.c + dc;
            const nk = `${nr},${nc}`;
            if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
            if (visited.has(nk)) continue;
            const typeB = this.grid[nr][nc];
            if (typeB === null) continue;
            if (!edgesCompatible(typeA, rotA, typeB, this.gridRotation[nr][nc], dir,
              this.gridFlipX[cur.r][cur.c], this.gridFlipY[cur.r][cur.c],
              this.gridFlipX[nr][nc], this.gridFlipY[nr][nc])) continue;
            visited.add(nk);
            queue.push({ r: nr, c: nc });
          }
        }
      }
    }
    return groups;
  }

  // ══════════════════════════════════════════
  //  CONNECTIONS DRAWING
  // ══════════════════════════════════════════

  drawConnections(): void {
    const { gridX, gridY, cellSize, rows, cols } = this.layout;
    this.connGfx.clear();
    const hs = cellSize / 2;
    const edgeOffset = hs - 3;
    const dotR = 4;
    const dashLen = 8;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const typeA = this.grid[r][c];
        if (typeA === null) continue;
        const cx = gridX + c * cellSize + hs;
        const cy = gridY + r * cellSize + hs;
        const rotA = this.gridRotation[r][c];
        const edges = getTransformedEdges(typeA, rotA, this.gridFlipX[r][c], this.gridFlipY[r][c]);
        const forbidden = getTransformedForbiddenEdges(typeA, rotA, this.gridFlipX[r][c], this.gridFlipY[r][c]);

        const positions = [
          { x: cx, y: cy - edgeOffset },
          { x: cx + edgeOffset, y: cy },
          { x: cx, y: cy + edgeOffset },
          { x: cx - edgeOffset, y: cy },
        ];
        for (let e = 0; e < 4; e++) {
          const { x, y } = positions[e];
          if (forbidden[e]) {
            const xs = 4;
            this.connGfx.lineStyle(2.5, 0xff2222, 0.7);
            this.connGfx.lineBetween(x - xs, y - xs, x + xs, y + xs);
            this.connGfx.lineBetween(x - xs, y + xs, x + xs, y - xs);
          } else if (edges[e]) {
            this.connGfx.fillStyle(0x44cc66, 0.6);
            this.connGfx.fillCircle(x, y, dotR);
            this.connGfx.lineStyle(1, 0x66ff88, 0.3);
            this.connGfx.strokeCircle(x, y, dotR);
          } else {
            this.connGfx.lineStyle(2.5, 0xff4444, 0.5);
            if (e === 0 || e === 2) {
              this.connGfx.lineBetween(x - dashLen / 2, y, x + dashLen / 2, y);
            } else {
              this.connGfx.lineBetween(x, y - dashLen / 2, x, y + dashLen / 2);
            }
          }
        }

        // Connection lines to right neighbor
        if (c + 1 < cols && this.grid[r][c + 1] !== null) {
          if (edgesCompatible(typeA, rotA, this.grid[r][c + 1]!, this.gridRotation[r][c + 1], 1,
            this.gridFlipX[r][c], this.gridFlipY[r][c], this.gridFlipX[r][c + 1], this.gridFlipY[r][c + 1])) {
            this.connGfx.lineStyle(3, 0x3388cc, 0.6);
            this.connGfx.lineBetween(cx + edgeOffset, cy, cx + cellSize - edgeOffset, cy);
            this.connGfx.fillStyle(0x55aaff, 0.7);
            this.connGfx.fillCircle(cx + hs, cy, 5);
          }
        }
        // Connection lines to bottom neighbor
        if (r + 1 < rows && this.grid[r + 1][c] !== null) {
          if (edgesCompatible(typeA, rotA, this.grid[r + 1][c]!, this.gridRotation[r + 1][c], 2,
            this.gridFlipX[r][c], this.gridFlipY[r][c], this.gridFlipX[r + 1][c], this.gridFlipY[r + 1][c])) {
            this.connGfx.lineStyle(3, 0x3388cc, 0.6);
            this.connGfx.lineBetween(cx, cy + edgeOffset, cx, cy + cellSize - edgeOffset);
            this.connGfx.fillStyle(0x55aaff, 0.7);
            this.connGfx.fillCircle(cx, cy + hs, 5);
          }
        }
      }
    }
    this.drawDirectionArrow();
  }

  /** Draw a large semi-transparent arrow across the grid showing movement direction. */
  private drawDirectionArrow(): void {
    this.dirArrowGfx.clear();
    const { gridX, gridY, gridW, gridH, cellSize, rows, cols } = this.layout;

    let dirRow = -1, dirCol = -1, dirRot = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] === ComponentType.DIRECTION) {
          dirRow = r; dirCol = c;
          dirRot = this.gridRotation[r][c];
          break;
        }
      }
      if (dirRow >= 0) break;
    }
    if (dirRow < 0) return;

    const gcx = gridX + gridW / 2;
    const gcy = gridY + gridH / 2;
    const arrowLen = Math.min(gridW, gridH) * 0.7;
    const shaftW = cellSize * 0.25;
    const headLen = cellSize * 0.6;
    const headW = cellSize * 0.5;
    const g = this.dirArrowGfx;

    const shaftStart = -arrowLen / 2;
    const shaftEnd = arrowLen / 2 - headLen;
    const tipX = arrowLen / 2;

    const shaftPts = [
      { x: shaftStart, y: -shaftW / 2 },
      { x: shaftEnd,   y: -shaftW / 2 },
      { x: shaftEnd,   y:  shaftW / 2 },
      { x: shaftStart, y:  shaftW / 2 },
    ];
    const headPts = [
      { x: shaftEnd, y: -headW },
      { x: tipX,     y:  0 },
      { x: shaftEnd, y:  headW },
    ];

    const cos = Math.cos(dirRot);
    const sin = Math.sin(dirRot);
    const tx = (p: { x: number; y: number }) => ({
      x: gcx + p.x * cos - p.y * sin,
      y: gcy + p.x * sin + p.y * cos,
    });

    const sT = shaftPts.map(tx);
    const hT = headPts.map(tx);

    g.fillStyle(0x88bbff, 0.12);
    g.beginPath();
    g.moveTo(sT[0].x, sT[0].y);
    for (let i = 1; i < sT.length; i++) g.lineTo(sT[i].x, sT[i].y);
    g.closePath();
    g.fillPath();

    g.lineStyle(1.5, 0x88bbff, 0.25);
    g.beginPath();
    g.moveTo(sT[0].x, sT[0].y);
    for (let i = 1; i < sT.length; i++) g.lineTo(sT[i].x, sT[i].y);
    g.closePath();
    g.strokePath();

    g.fillStyle(0x88bbff, 0.18);
    g.fillTriangle(hT[0].x, hT[0].y, hT[1].x, hT[1].y, hT[2].x, hT[2].y);

    g.lineStyle(1.5, 0x88bbff, 0.3);
    g.beginPath();
    g.moveTo(hT[0].x, hT[0].y);
    g.lineTo(hT[1].x, hT[1].y);
    g.lineTo(hT[2].x, hT[2].y);
    g.closePath();
    g.strokePath();
  }

  // ══════════════════════════════════════════
  //  OVERLAYS & TOASTS
  // ══════════════════════════════════════════

  /** Show red overlay on isolated (disconnected) components. */
  updateIsolatedOverlays(connectedCells: Set<string>, occupied: { r: number; c: number }[]): void {
    for (const ov of this.isolatedOverlays) ov.destroy();
    this.isolatedOverlays = [];
    const { gridX, gridY, cellSize } = this.layout;

    if (occupied.length <= 1) return;

    for (const { r, c } of occupied) {
      if (!connectedCells.has(`${r},${c}`)) {
        const cx = gridX + c * cellSize + cellSize / 2;
        const cy = gridY + r * cellSize + cellSize / 2;
        const overlay = this.scene.add.rectangle(cx, cy, cellSize - 2, cellSize - 2, 0xff0000, 0.25)
          .setStrokeStyle(2, 0xff4444, 0.7)
          .setDepth(8);
        this.isolatedOverlays.push(overlay);
      }
    }

    // Forbidden edge violations
    for (const { r, c } of occupied) {
      const type = this.grid[r][c]!;
      const forbidden = getTransformedForbiddenEdges(
        type, this.gridRotation[r][c], this.gridFlipX[r][c], this.gridFlipY[r][c]);
      const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]];
      let hasForbidden = false;
      const { rows, cols } = this.layout;
      for (const [dr, dc, dir] of dirs) {
        if (!forbidden[dir]) continue;
        const nr = r + dr, nc = c + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        if (this.grid[nr][nc] !== null) { hasForbidden = true; break; }
      }
      if (hasForbidden) {
        const cx = gridX + c * cellSize + cellSize / 2;
        const cy = gridY + r * cellSize + cellSize / 2;
        const overlay = this.scene.add.rectangle(cx, cy, cellSize - 2, cellSize - 2, 0xff8800, 0.2)
          .setStrokeStyle(2, 0xffaa44, 0.6)
          .setDepth(8);
        this.isolatedOverlays.push(overlay);
      }
    }
  }

  /** Flash isolated components red and show a blocking toast when saving with disconnected parts. */
  flashIsolatedComponents(): void {
    SoundManager.play('clickCancel');
    const { connected, occupied } = this.getConnectedCells();
    const { gridX, gridY, gridW, cellSize } = this.layout;

    for (const { r, c } of occupied) {
      if (connected.has(`${r},${c}`)) continue;
      const cx = gridX + c * cellSize + cellSize / 2;
      const cy = gridY + r * cellSize + cellSize / 2;
      const flash = this.scene.add.rectangle(cx, cy, cellSize - 2, cellSize - 2, 0xff0000, 0.6)
        .setStrokeStyle(2, 0xff2222, 1).setDepth(12);
      this.scene.tweens.add({
        targets: flash, alpha: 0, duration: 600, yoyo: true, repeat: 1,
        onComplete: () => flash.destroy(),
      });
    }

    const groupCount = this.countDisconnectedGroups();
    const msg = groupCount > 2
      ? `\u26A0 ${groupCount} isolated groups! Connect all components.`
      : `\u26A0 Isolated components! Connect all parts before saving.`;
    const toast = this.scene.add.text(gridX + gridW / 2, gridY - 18, msg, {
      fontSize: '11px', color: '#ff6644', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#220000', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(50);
    this.scene.tweens.add({
      targets: toast, alpha: 0, duration: 400, delay: 2200,
      onComplete: () => toast.destroy(),
    });
  }

  showQuickToast(msg: string): void {
    const { gridX, gridY, gridW } = this.layout;
    const toast = this.scene.add.text(gridX + gridW / 2, gridY - 18, `\u26A0 ${msg}`, {
      fontSize: '11px', color: '#ff6644', fontFamily: 'monospace', fontStyle: 'bold',
      backgroundColor: '#220000', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(50);
    this.scene.tweens.add({
      targets: toast, alpha: 0, duration: 400, delay: 1800,
      onComplete: () => toast.destroy(),
    });
  }

  // ══════════════════════════════════════════
  //  GRID HELPERS
  // ══════════════════════════════════════════

  clearGrid(callbacks: PlacementCallbacks): void {
    const { rows, cols } = this.layout;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        this.removeFromGrid(r, c, callbacks);
      }
    }
  }

  /** Rotate all components 90° clockwise around the center cell. */
  rotateAll90(placeOnGrid: (r: number, c: number, type: ComponentType, rotation: number, skipValidation: boolean, skipCost: boolean, evoLevel: number) => boolean, callbacks: PlacementCallbacks): void {
    const { rows, cols } = this.layout;
    const centerR = Math.floor(rows / 2);
    const centerC = Math.floor(cols / 2);

    const snapshot: { r: number; c: number; type: ComponentType; rot: number; fx: boolean; fy: boolean; evo: number }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] !== null) {
          snapshot.push({ r, c, type: this.grid[r][c]!, rot: this.gridRotation[r][c], fx: this.gridFlipX[r][c], fy: this.gridFlipY[r][c], evo: this.gridEvoLevel[r][c] });
        }
      }
    }

    this.clearGrid(callbacks);

    for (const { r, c, type, rot, fx, fy, evo } of snapshot) {
      const dr = r - centerR;
      const dc = c - centerC;
      const newR = centerR + dc;
      const newC = centerC - dr;
      if (newR < 0 || newR >= rows || newC < 0 || newC >= cols) continue;
      const newRot = rot + Math.PI / 2;
      placeOnGrid(newR, newC, type, newRot, true, true, evo);
      this.gridFlipX[newR][newC] = fx;
      this.gridFlipY[newR][newC] = fy;
      const visual = this.gridCellGfx[newR]?.[newC];
      if (visual) {
        if (fx) visual.scaleX *= -1;
        if (fy) visual.scaleY *= -1;
      }
    }
  }

  loadComponentsToGrid(
    components: { type: ComponentType; lx: number; ly: number; rotation?: number; flipX?: boolean; flipY?: boolean; evoLevel?: number }[],
    skipCost: boolean,
    placeOnGrid: (r: number, c: number, type: ComponentType, rotation: number, skipValidation: boolean, skipCost: boolean, evoLevel: number) => boolean,
    recalcStats: () => void,
  ): void {
    const { rows, cols, gridX, gridY, cellSize } = this.layout;
    const cells = components.map(comp => ({
      c: Math.round(comp.lx / COMPONENT_SPACING),
      r: Math.round(comp.ly / COMPONENT_SPACING),
      type: comp.type,
      rotation: comp.rotation ?? 0,
      flipX: comp.flipX ?? false,
      flipY: comp.flipY ?? false,
      evoLevel: comp.evoLevel ?? 0,
    }));

    const minC = Math.min(...cells.map(p => p.c));
    const maxC = Math.max(...cells.map(p => p.c));
    const minR = Math.min(...cells.map(p => p.r));
    const maxR = Math.max(...cells.map(p => p.r));
    const tw = maxC - minC + 1;
    const th = maxR - minR + 1;
    const offsetC = Math.floor((cols - tw) / 2) - minC;
    const offsetR = Math.floor((rows - th) / 2) - minR;

    for (const cell of cells) {
      const c = cell.c + offsetC;
      const r = cell.r + offsetR;
      if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
      placeOnGrid(r, c, cell.type, cell.rotation, true, skipCost, cell.evoLevel);
      if (cell.flipX || cell.flipY) {
        this.gridFlipX[r][c] = cell.flipX;
        this.gridFlipY[r][c] = cell.flipY;
        const visual = this.gridCellGfx[r]?.[c];
        if (visual) {
          if (cell.flipX) visual.scaleX *= -1;
          if (cell.flipY) visual.scaleY *= -1;
        }
      }
    }

    // Auto-rotate components with broken edge connections
    let changed = true;
    let passes = 0;
    while (changed && passes < 4) {
      changed = false;
      passes++;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const type = this.grid[r][c];
          if (type === null) continue;
          if (type === ComponentType.DIRECTION) continue;
          const rot = this.gridRotation[r][c];
          // Use inline edge check (same as hasValidEdgeConnection but without PlacementValidator reference)
          let hasAnyOther = false;
          for (let r2 = 0; r2 < rows; r2++) {
            for (let c2 = 0; c2 < cols; c2++) {
              if (this.grid[r2][c2] !== null && (r2 !== r || c2 !== c)) { hasAnyOther = true; break; }
            }
            if (hasAnyOther) break;
          }
          if (!hasAnyOther) continue;

          const dirs: [number, number, number][] = [[-1, 0, 0], [0, 1, 1], [1, 0, 2], [0, -1, 3]];
          let hasConn = false;
          for (const [dr, dc, dir] of dirs) {
            const nr = r + dr, nc = c + dc;
            if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
            const nb = this.grid[nr][nc];
            if (nb === null) continue;
            if (edgesCompatible(type, rot, nb, this.gridRotation[nr][nc], dir,
              this.gridFlipX[r][c], this.gridFlipY[r][c],
              this.gridFlipX[nr][nc], this.gridFlipY[nr][nc])) {
              hasConn = true; break;
            }
          }
          if (hasConn) continue;

          for (let step = 1; step <= 3; step++) {
            const tryRot = rot + step * (Math.PI / 2);
            let tryConn = false;
            for (const [dr, dc, dir] of dirs) {
              const nr = r + dr, nc = c + dc;
              if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
              const nb = this.grid[nr][nc];
              if (nb === null) continue;
              if (edgesCompatible(type, tryRot, nb, this.gridRotation[nr][nc], dir,
                this.gridFlipX[r][c], this.gridFlipY[r][c],
                this.gridFlipX[nr][nc], this.gridFlipY[nr][nc])) {
                tryConn = true; break;
              }
            }
            if (tryConn) {
              this.gridRotation[r][c] = tryRot;
              const visual = this.gridCellGfx[r]?.[c];
              if (visual) {
                applyGridRotationFn(visual, type, tryRot, this.gridEvoLevel[r][c]);
                const cellCx = gridX + c * cellSize + cellSize / 2;
                const cellCy = gridY + r * cellSize + cellSize / 2;
                positionInCellFn(visual, cellCx, cellCy, type, tryRot, cellSize, this.gridEvoLevel[r][c]);
              }
              changed = true;
              break;
            }
          }
        }
      }
    }

    recalcStats();
  }
}
